import React from 'react';
import { List } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Stepper } from '@/components/ui/stepper';
import { cn } from '@/lib/utils';

export default function DirectorShell({
    currentPhase,
    phaseOptions,
    onPhaseChange,
    currentJobId,
    isMockEnabled = false,
    onChangeBusiness,
    showSidebarToggle = false,
    onSidebarToggle,
    children,
}) {
    const currentStepIndex = Math.max(
        0,
        phaseOptions.findIndex((phase) => phase.value === currentPhase),
    );
    const currentPhaseMeta = phaseOptions[currentStepIndex] || phaseOptions[0];

    const renderMobilePhaseChip = (phase, index) => {
        const isActive = phase.value === currentPhase;
        const isComplete = index < currentStepIndex;

        return (
            <button
                key={phase.value}
                type="button"
                onClick={() => onPhaseChange?.(phase.value)}
                disabled={phase.disabled}
                className={cn(
                    'flex min-w-[4.875rem] shrink-0 items-center gap-1.5 rounded-xl border px-2.5 py-1.5 text-left transition-colors',
                    isActive
                        ? 'border-coral/30 bg-coral/10 text-coral shadow-sm'
                        : 'border-border bg-background text-foreground hover:bg-muted/60',
                    isComplete && !isActive && 'border-coral/20 text-coral',
                    phase.disabled && 'cursor-not-allowed border-border/70 bg-muted/40 text-muted-foreground hover:bg-muted/40',
                )}
                aria-current={isActive ? 'step' : undefined}
            >
                <span
                    className={cn(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                        isActive
                            ? 'bg-coral text-white'
                            : isComplete
                                ? 'bg-coral/15 text-coral'
                                : 'bg-muted text-muted-foreground',
                    )}
                >
                    {index + 1}
                </span>
                <span className="min-w-0 truncate text-[13px] font-medium">
                    {phase.shortTitle || phase.title}
                </span>
            </button>
        );
    };

    return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background text-foreground">
            <div className="sticky top-0 z-40 shrink-0 border-b border-border bg-card/95 backdrop-blur-sm shadow-[0_1px_3px_0_rgba(0,0,0,0.04)]">
                <div className="mx-auto hidden w-full max-w-[1720px] grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4 px-4 py-2.5 md:grid sm:px-6">
                    <div className="flex min-w-0 items-center gap-2.5">
                        <span className="truncate text-sm font-semibold text-foreground sm:text-base">
                            Director Studio
                        </span>
                        <Badge className="border-amber-300 bg-amber-50 text-amber-600">BETA</Badge>
                        {isMockEnabled ? (
                            <Badge className="border-blue-300 bg-blue-50 text-blue-600">MOCK MODE</Badge>
                        ) : null}
                        {currentJobId && onChangeBusiness ? (
                            <Button
                                variant="outline"
                                size="sm"
                                className="hidden xl:inline-flex"
                                onClick={onChangeBusiness}
                            >
                                Change Business
                            </Button>
                        ) : null}
                    </div>

                    <div className="flex min-w-0 justify-center">
                        <Stepper
                            steps={phaseOptions.map((phase) => ({ title: phase.shortTitle || phase.title }))}
                            currentStep={currentStepIndex}
                            onStepClick={(index) => onPhaseChange?.(phaseOptions[index]?.value)}
                            className="max-w-full"
                        />
                    </div>

                    <div className="flex min-w-0 items-center justify-end gap-2">
                        {currentJobId && onChangeBusiness ? (
                            <Button
                                variant="outline"
                                size="sm"
                                className="xl:hidden"
                                onClick={onChangeBusiness}
                            >
                                Change Business
                            </Button>
                        ) : null}
                        {currentJobId ? (
                            <span className="truncate text-xs text-muted-foreground">
                                ID: {currentJobId}
                            </span>
                        ) : null}
                        {showSidebarToggle && onSidebarToggle ? (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="lg:hidden"
                                onClick={onSidebarToggle}
                                aria-label="Open story stream"
                            >
                                <List className="h-4 w-4" />
                            </Button>
                        ) : null}
                    </div>
                </div>

                <div className="mx-auto flex w-full max-w-[1720px] flex-col gap-2.5 px-4 py-2.5 md:hidden sm:px-6">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                            <div className="flex min-w-0 items-center gap-2">
                                <span className="truncate text-sm font-semibold text-foreground">
                                    Director Studio
                                </span>
                                <Badge className="border-amber-300 bg-amber-50 text-amber-600">BETA</Badge>
                                {isMockEnabled ? (
                                    <Badge className="border-blue-300 bg-blue-50 text-blue-600">MOCK</Badge>
                                ) : null}
                            </div>

                            <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                                <span className="font-medium text-foreground">
                                    Step {currentStepIndex + 1} of {phaseOptions.length}
                                </span>
                                <span className="rounded-full bg-muted px-2 py-1">
                                    {currentPhaseMeta?.title}
                                </span>
                                {currentJobId ? (
                                    <span className="max-w-[13rem] truncate">
                                        ID: {currentJobId}
                                    </span>
                                ) : null}
                            </div>
                        </div>

                        {currentJobId && onChangeBusiness ? (
                            <Button variant="outline" size="sm" className="h-8 shrink-0 px-3 text-xs" onClick={onChangeBusiness}>
                                Change Business
                            </Button>
                        ) : null}
                    </div>

                    <div className="-mx-1 overflow-x-auto px-1 pt-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        <div className="flex min-w-max items-center gap-2">
                            {phaseOptions.map((phase, index) => renderMobilePhaseChip(phase, index))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {children}
            </div>
        </div>
    );
}
